import { useEffect, useState } from 'react';
import {
  listAdminCustomers,
  getAdminCustomerOrders,
  getAdminCustomerLicenses,
  type AdminCustomer,
  type AdminCustomerOrder,
  type AdminCustomerLicense,
} from '@/services/users';

function CustomerModal({ customer, onClose }: { customer: AdminCustomer; onClose: () => void }) {
  const [orders, setOrders] = useState<AdminCustomerOrder[]>([]);
  const [licenses, setLicenses] = useState<AdminCustomerLicense[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([getAdminCustomerOrders(customer.id), getAdminCustomerLicenses(customer.id)])
      .then(([o, l]) => { setOrders(o); setLicenses(l); })
      .finally(() => setLoading(false));
  }, [customer.id]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-xl border border-surface-200 shadow-xl w-full max-w-2xl max-h-[85vh] flex flex-col overflow-hidden">
        <div className="flex items-start gap-4 px-6 py-5 border-b border-surface-200">
          <div className="w-12 h-12 rounded-full bg-brand-100 flex items-center justify-center text-brand-700 font-bold text-lg shrink-0">
            {customer.name.charAt(0).toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-surface-900 text-lg">{customer.name}</h3>
            <p className="text-sm text-surface-500 mt-0.5">{customer.email}</p>
            <div className="flex items-center gap-2 mt-2">
              <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium capitalize ${customer.role === 'admin' ? 'bg-purple-50 text-purple-700' : 'bg-blue-50 text-blue-700'}`}>
                {customer.role}
              </span>
              <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium capitalize ${customer.status === 'active' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                {customer.status}
              </span>
              <span className="text-xs text-surface-400">Joined {new Date(customer.createdAt).toLocaleDateString()}</span>
            </div>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-surface-100 text-surface-500 shrink-0">
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        <div className="px-6 py-5 overflow-auto space-y-6">
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-surface-50 rounded-lg p-3">
              <p className="text-xs text-surface-500">Total Orders</p>
              <p className="font-semibold text-surface-900 text-lg mt-0.5">{customer.orderCount}</p>
            </div>
            <div className="bg-surface-50 rounded-lg p-3">
              <p className="text-xs text-surface-500">Total Licenses</p>
              <p className="font-semibold text-surface-900 text-lg mt-0.5">{customer.licenseCount}</p>
            </div>
          </div>

          {loading ? (
            <p className="text-sm text-surface-500">Loading details...</p>
          ) : (
            <>
              <div>
                <h4 className="text-sm font-semibold text-surface-900 mb-3">Recent Orders</h4>
                {orders.length === 0 ? <p className="text-sm text-surface-400">No orders yet.</p> : (
                  <div className="space-y-2">
                    {orders.map((order) => (
                      <div key={order.id} className="flex items-center justify-between bg-surface-50 rounded-lg px-3 py-2.5">
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-surface-900 truncate">{order.productName}</p>
                          <p className="text-xs text-surface-500 mt-0.5">{order.orderRef}</p>
                        </div>
                        <div className="flex items-center gap-3 shrink-0 ml-3">
                          <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium capitalize ${order.status === 'paid' ? 'bg-green-50 text-green-700' : order.status === 'pending' ? 'bg-amber-50 text-amber-700' : 'bg-red-50 text-red-700'}`}>
                            {order.status}
                          </span>
                          <span className="text-sm font-semibold text-surface-900">${order.amount}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <h4 className="text-sm font-semibold text-surface-900 mb-3">Recent Licenses</h4>
                {licenses.length === 0 ? <p className="text-sm text-surface-400">No licenses yet.</p> : (
                  <div className="space-y-2">
                    {licenses.map((license) => (
                      <div key={license.id} className="flex items-center justify-between bg-surface-50 rounded-lg px-3 py-2.5">
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-surface-900 truncate">{license.productName}</p>
                          <p className="text-xs text-surface-500 mt-0.5 font-mono">****{license.licenseKeyLast4}</p>
                        </div>
                        <div className="flex items-center gap-3 shrink-0 ml-3">
                          <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium capitalize ${license.status === 'active' ? 'bg-green-50 text-green-700' : license.status === 'expired' ? 'bg-amber-50 text-amber-700' : 'bg-red-50 text-red-700'}`}>
                            {license.status}
                          </span>
                          <span className="text-xs text-surface-500">{license.expiresAt ? new Date(license.expiresAt).toLocaleDateString() : 'Never'}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        <div className="px-6 py-4 border-t border-surface-200 flex justify-end">
          <button onClick={onClose} className="px-4 py-2 rounded-lg bg-brand-600 text-white text-sm font-medium hover:bg-brand-700 transition-colors">Close</button>
        </div>
      </div>
    </div>
  );
}

export default function AdminCustomers() {
  const [customers, setCustomers] = useState<AdminCustomer[]>([]);
  const [search, setSearch] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState<AdminCustomer | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError('');
    listAdminCustomers()
      .then((data) => { if (alive) setCustomers(data); })
      .catch((err) => { if (alive) setError(err?.message ?? 'Unable to load customers'); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  const filtered = customers.filter((c) =>
    !search ||
    c.name.toLowerCase().includes(search.toLowerCase()) ||
    c.email.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-xl md:text-2xl font-bold text-surface-900">Users</h1>
        <p className="text-sm text-surface-500 mt-1">Manage all user accounts.</p>
      </div>

      {error && <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">{error}</div>}

      <div className="relative max-w-md">
        <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-surface-400" />
        <input type="text" placeholder="Search users..." value={search} onChange={(e) => setSearch(e.target.value)} className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-surface-300 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 bg-white" />
      </div>

      <div className="bg-white rounded-xl border border-surface-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-surface-200">
                <th className="text-left px-5 py-3 font-medium text-surface-500 text-xs">User</th>
                <th className="text-left px-5 py-3 font-medium text-surface-500 text-xs">Email</th>
                <th className="text-left px-5 py-3 font-medium text-surface-500 text-xs">Role</th>
                <th className="text-left px-5 py-3 font-medium text-surface-500 text-xs">Orders</th>
                <th className="text-left px-5 py-3 font-medium text-surface-500 text-xs">Licenses</th>
                <th className="text-left px-5 py-3 font-medium text-surface-500 text-xs">Status</th>
                <th className="text-left px-5 py-3 font-medium text-surface-500 text-xs">Joined</th>
                <th className="text-left px-5 py-3 font-medium text-surface-500 text-xs">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={8} className="px-5 py-8 text-center text-surface-500">Loading users...</td></tr>}
              {!loading && filtered.length === 0 && <tr><td colSpan={8} className="px-5 py-8 text-center text-surface-500">No users found.</td></tr>}
              {filtered.map((customer) => (
                <tr key={customer.id} className="border-b border-surface-50 hover:bg-surface-50/50">
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-brand-100 flex items-center justify-center text-brand-700 font-semibold text-sm">
                        {customer.name.charAt(0).toUpperCase()}
                      </div>
                      <span className="font-semibold text-surface-900">{customer.name}</span>
                    </div>
                  </td>
                  <td className="px-5 py-3 text-surface-600">{customer.email}</td>
                  <td className="px-5 py-3">
                    <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium capitalize ${customer.role === 'admin' ? 'bg-purple-50 text-purple-700' : 'bg-blue-50 text-blue-700'}`}>
                      {customer.role}
                    </span>
                  </td>
                  <td className="px-5 py-3 font-semibold text-surface-900">{customer.orderCount}</td>
                  <td className="px-5 py-3 font-semibold text-surface-900">{customer.licenseCount}</td>
                  <td className="px-5 py-3">
                    <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium capitalize ${customer.status === 'active' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                      {customer.status}
                    </span>
                  </td>
                  <td className="px-5 py-3 text-surface-500">{new Date(customer.createdAt).toLocaleDateString()}</td>
                  <td className="px-5 py-3">
                    <button onClick={() => setSelectedCustomer(customer)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-surface-100 text-surface-500 transition-colors">
                      <i className="ri-eye-line" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {selectedCustomer && <CustomerModal customer={selectedCustomer} onClose={() => setSelectedCustomer(null)} />}
    </div>
  );
}
